/**
 * Site registry — single source of truth for every brand this app builds.
 *
 * The app is ONE codebase deployed as multiple "sites" (LV, Venetian, Demo).
 * A site is just a small config blob; everything else is shared code.
 * Adding a site = add one entry here + one env file + one hosting target.
 *
 * Selector precedence (build-time, set in the per-site env file):
 *   1. REACT_APP_SITE                  — e.g. "lv" | "venetian" | "demo" (preferred)
 *   2. REACT_APP_FIREBASE_DATABASE_ID  — legacy raw DB id, still honored
 *   3. default → demo                  — safe public default
 */
export const SITES = {
  lv: {
    key: 'lv',
    databaseId: 'booking-lv',
    brand: 'LV Nail Booking',
    demo: false,
    // Business hours drive the public booking form's time slots (per day).
    businessHours: {
      monday:    { start: '09:30', end: '19:30' },
      tuesday:   { start: '09:30', end: '19:30' },
      wednesday: { start: '09:30', end: '19:30' },
      thursday:  { start: '09:30', end: '19:30' },
      friday:    { start: '09:30', end: '19:30' },
      saturday:  { start: '09:30', end: '18:30' },
      sunday:    { start: '10:00', end: '16:00' },
    },
  },
  venetian: {
    key: 'venetian',
    databaseId: 'booking-venetian',
    brand: 'Venetian Nail Booking',
    demo: false,
    // Business hours drive the public booking form's time slots (per day).
    businessHours: {
      monday:    { start: '09:00', end: '19:00' },
      tuesday:   { start: '09:00', end: '19:00' },
      wednesday: { start: '09:00', end: '19:00' },
      thursday:  { start: '09:00', end: '19:00' },
      friday:    { start: '09:00', end: '19:00' },
      saturday:  { start: '09:00', end: '19:00' },
      sunday:    { start: '10:00', end: '17:00' },
    },
  },
  pro: {
    key: 'pro',
    databaseId: 'booking-pro',
    brand: 'Pro Nails Booking',
    demo: false,
  },
  demo: {
    key: 'demo',
    databaseId: 'demo',
    brand: 'Nail Booking Demo',
    demo: true,
  },
};

const activeKey = String(process.env.REACT_APP_SITE || '').trim().toLowerCase();
const legacyDbId = String(process.env.REACT_APP_FIREBASE_DATABASE_ID || '').trim().toLowerCase();

/** Resolve the active site for this build. */
export const getSite = () => {
  const matched =
    SITES[activeKey] ||
    Object.values(SITES).find((s) => s.databaseId === legacyDbId) ||
    (legacyDbId
      ? { key: legacyDbId, databaseId: legacyDbId, brand: 'Nail Booking', demo: false }
      : SITES.demo);

  // Fail loud instead of silently shipping the wrong brand/DB.
  if (activeKey && !SITES[activeKey]) {
    console.warn(
      `[sites] Unknown REACT_APP_SITE "${activeKey}" — resolved to "${matched.key}" (${matched.databaseId}). ` +
        `Valid keys: ${Object.keys(SITES).join(', ')}`
    );
  }

  return matched;
};
